<template>
  <div>
    <template v-for="(field, index) in infoSection.fields">
      <div v-if="index % 2 === 0" :key="`info-${index}`" class="row b-b1-override">
        <template v-for="(currentField, fieldIndex) in [field, infoSection.fields[index + 1]]">
          <div v-if="currentField" :key="`info-${index}-${fieldIndex}`" :class="['data f-1', { 'm-lx3': fieldIndex === 1, 'm-rx3': fieldIndex === 0 }]">
            <div class="f-h f-ac f-sb">
              <div class="t m-rx2">
                {{ currentField.text }}
                <span v-if="currentField.subtext" class="t-sm gray">{{ currentField.subtext }}</span>
              </div>
              <div v-if="currentField.id && currentField.input_type !== 'not_editable'">
                <donut-form-input
                  :for="currentField.value.value"
                  :name="`field_${currentField.id}`"
                  :validators="currentField.validators"
                >
                  <donut-input-text
                    v-if="currentField.input_type == 'text'"
                    v-model="currentField.value.value"
                    :disabled="formSubmitted"
                  />
                  <donut-datepicker
                    v-else-if="currentField.input_type == 'date'"
                    v-model="currentField.value.value"
                    :clear-btn="true"
                    format="yyyy-mm-dd"
                    :disabled="formSubmitted"
                  />
                  <donut-selex-users
                    v-else-if="currentField.input_type == 'user'"
                    v-model="currentField.value.value.related_user"
                    placeholder="Choose manager"
                    :disabled="formSubmitted || (currentField.value?.value?.source && currentField.value.value.source != 'user')"
                    :include-empty-value="true"
                    :can-be-cleared="currentField.value?.value?.source === 'user'"
                    :update-action="updateManagerRelationship(currentField)"
                  />
                </donut-form-input>
              </div>
              <div v-if="currentField.input_type === 'not_editable' && currentField.id !== 'reports_to'" class="t">
                {{ currentField.value.value }}
              </div>
              <div v-if="currentField.input_type === 'not_editable' && currentField.id === 'reports_to'" class="t">
                {{ currentField.value.value.related_user?.name }}
              </div>
            </div>
          </div>
          <div v-else :key="`empty-${fieldIndex}`" class="data f-1 m-lx3" />
        </template>
      </div>
    </template>
    <div class="row b-b1-override">
      <div class="data f-1">
        <div v-for="currentField in bioSection.fields" :key="`bio-${currentField.id}`">
          <div class="t">{{ currentField.text }}</div>
          <div v-if="currentField.id" class="m-tx1">
            <donut-form-input
              :for="currentField.value.value"
              :name="`field_${currentField.id}`"
              :validators="currentField.validators"
            >
              <donut-input-text
                v-model="currentField.value.value"
                :textarea="true"
                :autogrow="true"
                :max-length="1000"
                :placeholder="currentField.placeholder"
                :disabled="formSubmitted"
                :wysiwyg="false"
              />
            </donut-form-input>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import useValidators from 'composables/validators';

const props = defineProps({
  profileEntries: {
    type: Array,
    required: true,
  },
  profileUser: {
    type: Object,
    required: true,
  },
  formSubmitted: {
    type: Boolean,
    default: false,
  },
});

const { getters } = useStore();
const { characterCountValidator, linkedInUrlValidator } = useValidators();

const updateManagerRelationship = (currentField) => (_id, user) => {
  currentField.value.value.related_user = user;
  currentField.value.value.related_user_id = user?.id;
};

const userManagerRelationship = computed(() => getters['userProfileFields/profileManagers'][0] || {});
const profileManagerSubtext = () => (userManagerRelationship.value?.source && userManagerRelationship.value.source !== 'user' ? `From ${userManagerRelationship.value.display_source}` : '');

// Create a local copy of the manager relationship to avoid mutating the store directly
// Deep clone to ensure nested objects like related_user are not shared
const localManagerRelationship = ref(
  userManagerRelationship.value ? JSON.parse(JSON.stringify(userManagerRelationship.value)) : {},
);

const infoSection = computed(() => {
  const fields = [];
  const validators = [characterCountValidator(250, 'This field')];
  // Helper to find entry by field machine_name
  const findEntry = (category, field) => {
    const cat = props.profileEntries.find(e => e.category?.machine_name === category);
    if (!cat) return null;
    return cat.entries.find(f => f.field.machine_name === field);
  };
  // Location field
  const locationEntry = findEntry('my_profile', 'location');
  fields.push({
    ...locationEntry?.field,
    input_type: 'text',
    validators,
    value: ref(locationEntry?.text ?? ''),
  });
  // Local time field (not editable)
  fields.push({
    text: 'Local time',
    subtext: 'From Slack',
    input_type: 'not_editable',
    value: ref(getters['userProfileFields/currentProfileUserTime'](props.profileUser) ?? ''),
  });
  // Team field
  const teamEntry = findEntry('my_profile', 'team');
  fields.push({
    ...teamEntry?.field,
    input_type: 'text',
    validators,
    value: ref(teamEntry?.text ?? ''),
  });
  fields.push({
    text: 'Job title',
    subtext: 'From Slack',
    input_type: 'not_editable',
    value: ref(props.profileUser.job_title ?? ''),
  });
  // Start date field
  if (!getters['celebrationSchedules/editingRestrictedTypes'].includes('anniversary')) {
    const startDateEntry = findEntry('my_profile', 'start_date');
    fields.push({
      ...startDateEntry?.field,
      input_type: 'date',
      value: ref(startDateEntry?.text ?? ''),
    });
  }
  // Report to or manager field
  fields.push({
    id: 'reports_to',
    text: 'Reports to',
    input_type: userManagerRelationship.value?.source && userManagerRelationship.value.source !== 'user' ? 'not_editable' : 'user',
    value: localManagerRelationship,
    subtext: profileManagerSubtext(),
  });
  // LinkedIn field
  const linkedinEntry = findEntry('my_profile', 'linkedin');
  fields.push({
    ...linkedinEntry?.field,
    input_type: 'text',
    value: ref(linkedinEntry?.text ?? ''),
    placeholder: 'Enter a valid LinkedIn URL',
    validators: validators.concat(linkedInUrlValidator()),
  });
  return {
    category: props.profileEntries.find(e => e.category?.machine_name === 'my_profile')?.category,
    fields,
  };
});

const bioSection = computed(() => {
  const bioEntry = props.profileEntries.find(e => e.category?.machine_name === 'bio');
  const field = bioEntry?.entries[0]?.field;
  return {
    category: bioEntry?.category,
    fields: [
      {
        ...field,
        input_type: 'text',
        value: ref(bioEntry?.entries[0]?.text ?? ''),
        placeholder: 'Tell your team something about yourself',
        validators: [characterCountValidator(2500, 'This field')],
      },
    ],
  };
});

defineExpose({
  infoSection,
  bioSection,
});
</script>
